import { computed, ref } from 'vue';
import type { ComputedRef, Ref } from 'vue';

import { buildLabel } from '../lib/buildLabel';
import  { DESTINATION_TEXTS } from '../config/texts';
import { computeInputHint } from '../lib/computeInputHint';
import { sortOptions } from '../lib/sortOptions';
import { getCountryResorts } from '../api/getCountryResorts';
import { getOptions } from '../api/getOptions';
import { useTravelataTracker } from '@/shared/composables/useTravelataTracker';
import type { Country, Resort, Hotel, Destination, DestinationPrices, OptionGroup } from './types';
import type { Selection } from './selection';
import {
  emptySelection,
  getSelectedCountry,
  getSelectedHotels,
  getSelectedResorts,
  isCountry,
  isHotels,
  isNone,
  isResorts,
  selectCountry as selectCountryTransition,
  toggleResort as toggleResortTransition,
  selectAllResorts as selectAllResortsTransition,
  selectHotel as selectHotelTransition,
} from './selection';

export interface UseDestination {
  inputString: Ref<string>;
  selection: Ref<Selection>;
  optionsList: Ref<OptionGroup[]>;
  controlActivated: Ref<boolean>;
  loading: Ref<boolean>;
  invalid: Ref<boolean>;
  errorText: Ref<string>;

  selectedCountry: ComputedRef<Country | null>;
  selectedResorts: ComputedRef<Resort[]>;
  selectedHotels: ComputedRef<Hotel[]>;
  firstOptionName: ComputedRef<string | null>;
  label: ComputedRef<string>;
  inputHint: ComputedRef<string>;
  originalLabelChanged: ComputedRef<boolean>;
  selectAllVisible: ComputedRef<boolean>;
  addMulti: ComputedRef<boolean>;
  currentInputSelected: ComputedRef<boolean>;
  clearVisible: ComputedRef<boolean>;

  scrollToTopSignal: Ref<number>;
  selectCountry: (country: Country, fromAutoSelect?: boolean) => Promise<void>;
  toggleResort: (resort: Resort, fromAutoSelect?: boolean) => Promise<void>;
  selectAllResorts: () => void;
  selectHotel: (hotel: Hotel) => void;
  selectDestination: (destination: Destination, fromAutoSelect?: boolean) => void;

  open: () => void;
  close: () => void;
  setInput: (v: string) => Promise<void>;
  acceptHint: () => void;
  clear: () => void;
  clearInput: () => void;
  validate: () => boolean;
  restore: (selection: Selection) => void;
  setCountries: (list: Country[]) => void;
  repriceResorts: () => void;
}

export interface UseDestinationOptions {
  initialCountries?: Country[];
  prices?: () => DestinationPrices | undefined;
}

export function useDestination(options: UseDestinationOptions = {}): UseDestination {
  const tracker = useTravelataTracker();

  const readPrices = (): DestinationPrices => options.prices?.() ?? {};

  const withCountryPrice = (country: Country): Country => {
    const external = readPrices().country?.[country.id];
    return { ...country, minPrice: external ?? country.minPrice ?? null };
  };

  const withResortPrice = (resort: Resort): Resort => {
    const external = readPrices().resort?.[resort.id];
    return { ...resort, minPrice: external ?? resort.minPrice ?? null };
  };

  const SEARCH_DEBOUNCE_MS = 300;
  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  const inputString = ref('');
  const selection = ref<Selection>(emptySelection());
  const optionsList = ref<OptionGroup[]>([]);
  const controlActivated = ref(false);
  const loading = ref(false);
  const invalid = ref(false);
  const errorText = ref('');
  const countries = ref<Country[]>((options.initialCountries ?? []).map(withCountryPrice));
  const scrollToTopSignal = ref(0);
  const currentCountryResortsSource = ref<Resort[]>([]);
  const currentCountryResorts = ref<Resort[]>([]);

  const selectedCountry = computed<Country | null>(() => getSelectedCountry(selection.value));
  const selectedResorts = computed<Resort[]>(() => getSelectedResorts(selection.value));
  const selectedHotels = computed<Hotel[]>(() => getSelectedHotels(selection.value));

  const firstOptionName = computed<string | null>(
    () => optionsList.value[0]?.items[0]?.name ?? null,
  );

  const label = computed<string>(() => buildLabel(selection.value));

  const inputHint = computed<string>(() =>
    computeInputHint(inputString.value, firstOptionName.value, controlActivated.value),
  );

  const originalLabelChanged = computed<boolean>(
    () => inputString.value.toLowerCase() !== label.value.toLowerCase(),
  );

  const selectAllVisible = computed<boolean>(
    () =>
      controlActivated.value &&
      inputString.value !== '' &&
      (isCountry(selection.value) || isResorts(selection.value)),
  );

  const addMulti = computed<boolean>(
    () =>
      (isResorts(selection.value) || isCountry(selection.value)) && !originalLabelChanged.value,
  );

  const currentInputSelected = computed<boolean>(() => {
    const current = selection.value;
    const input = inputString.value;

    if (input === '') {
      return false;
    }

    let singleItemName: string | null = null;
    if (isCountry(current)) {
      singleItemName = current.country.name;
    } else if (isResorts(current) && current.resorts.length === 1) {
      singleItemName = current.resorts[0].name;
    } else if (isHotels(current) && current.hotels.length === 1) {
      singleItemName = current.hotels[0].name;
    }

    if (singleItemName === null) {
      return false;
    }

    return singleItemName.toLowerCase() === input.toLowerCase();
  });

  const clearVisible = computed<boolean>(
    () => inputString.value !== '' && controlActivated.value && !isNone(selection.value),
  );

  const selectedIds = (): Set<number> =>
    new Set<number>([
      ...selectedResorts.value.map((r) => r.id),
      ...selectedHotels.value.map((h) => h.id),
      ...(selectedCountry.value ? [selectedCountry.value.id] : []),
    ]);

  const rebuildEmptyList = (): void => {
    const items: Destination[] = countries.value.map((c) => ({ type: 'country' as const, ...c }));
    optionsList.value = sortOptions(items, selectedIds(), { pinSelected: true });
  };

  const maybeTrackSelection = (selectedData: Destination): void => {
    if (originalLabelChanged.value && inputString.value !== '') {
      tracker?.trackIntelligentSearch({
        inputValue: inputString.value,
        selectedData: { id: selectedData.id, name: selectedData.name, type: selectedData.type },
      });
    }
  };

  const selectDestination = (destination: Destination, fromAutoSelect = false): void => {
    if (destination.type === 'country') {
      const { type: _type, ...country } = destination;
      void selectCountry(country, fromAutoSelect);
    } else if (destination.type === 'resort') {
      const { type: _type, ...resort } = destination;
      void toggleResort(resort, fromAutoSelect);
    } else {
      const { type: _type, ...hotel } = destination;
      selectHotel(hotel);
    }
  };

  const loadCountryResorts = async (country: Country): Promise<Resort[]> => {
    loading.value = true;
    const source = await getCountryResorts(country);
    currentCountryResortsSource.value = source;
    const resorts = source.map(withResortPrice);
    currentCountryResorts.value = resorts;
    const items: Destination[] = resorts.map((r) => ({ type: 'resort' as const, ...r }));
    optionsList.value = sortOptions(items, selectedIds());
    loading.value = false;
    return resorts;
  };

  const rebuildCountryResorts = (country: Country): void => {
    const cached = currentCountryResorts.value;
    if (cached.length > 0 && cached[0].countryId === country.id) {
      const items: Destination[] = cached.map((r) => ({ type: 'resort' as const, ...r }));
      optionsList.value = sortOptions(items, selectedIds());
    } else {
      void loadCountryResorts(country);
    }
  };

  const syncInputToLabel = (): void => {
    inputString.value = label.value;
  };

  const resolveCountry = (countryId: number, fallbackName: string): Country => {
    if (selectedCountry.value && selectedCountry.value.id === countryId) {
      return selectedCountry.value;
    }
    const known = countries.value.find((c) => c.id === countryId);
    if (known) {
      return known;
    }
    return {
      id: countryId,
      name: fallbackName,
      isPopular: false,
      position: null,
      visaRequired: false,
      visaOnline: false,
      visaDays: null,
      minPrice: null,
    };
  };

  const selectCountry = async (country: Country, fromAutoSelect = false): Promise<void> => {
    maybeTrackSelection({ type: 'country', ...country });
    selection.value = selectCountryTransition(country);
    if (!(fromAutoSelect && controlActivated.value)) {
      syncInputToLabel();
    }
    const resorts = await loadCountryResorts(country);
    if (
      resorts.length > 0 &&
      isCountry(selection.value) &&
      selection.value.country.id === country.id
    ) {
      selection.value = selectCountryTransition(country, resorts);
    }
    scrollToTopSignal.value += 1;
  };

  const toggleResort = async (resort: Resort, fromAutoSelect = false): Promise<void> => {
    maybeTrackSelection({ type: 'resort', ...resort });
    const country: Country = resolveCountry(resort.countryId, resort.countryName);

    const previousCountryId = selectedCountry.value?.id ?? null;
    selection.value = toggleResortTransition(
      selection.value,
      resort,
      country,
      currentCountryResorts.value,
    );
    if (!(fromAutoSelect && controlActivated.value)) {
      syncInputToLabel();
    }

    if (previousCountryId !== resort.countryId) {
      await loadCountryResorts(country);
    } else {
      const items = optionsList.value.flatMap((g) => g.items);
      optionsList.value = sortOptions(items, selectedIds());
    }
  };

  const selectAllResorts = (): void => {
    if (selectedCountry.value) {
      selection.value = selectAllResortsTransition(selectedCountry.value, currentCountryResorts.value);
      syncInputToLabel();
    }
  };

  const selectHotel = (hotel: Hotel): void => {
    maybeTrackSelection({ type: 'hotel', ...hotel });
    const country: Country = resolveCountry(hotel.countryId, hotel.countryName);
    selection.value = selectHotelTransition(selection.value, hotel, country);
    syncInputToLabel();
    controlActivated.value = false;
  };

  const open = (): void => {
    controlActivated.value = true;
    if (inputString.value === '') {
      rebuildEmptyList();
      return;
    }
    const current = selection.value;
    if ((isCountry(current) || isResorts(current)) && !originalLabelChanged.value) {
      rebuildCountryResorts(current.country);
    }
  };

  const close = (): void => {
    controlActivated.value = false;
    errorText.value = '';
    invalid.value = false;
    if (!isNone(selection.value)) {
      inputString.value = label.value;
    }
  };

  const runSearch = async (v: string): Promise<void> => {
    const results = await getOptions(v);
    optionsList.value = sortOptions(results, selectedIds());
    invalid.value = results.length === 0;

    const first = results[0];
    if (first && first.name.toLowerCase() === v.toLowerCase()) {
      selectDestination(first, true);
    }
  };

  const scheduleSearch = (v: string): Promise<void> =>
    new Promise<void>((resolve) => {
      if (searchTimer !== null) {
        clearTimeout(searchTimer);
      }
      searchTimer = setTimeout(() => {
        searchTimer = null;
        void runSearch(v).then(resolve);
      }, SEARCH_DEBOUNCE_MS);
    });

  const setInput = async (v: string): Promise<void> => {
    inputString.value = v;

    if (v === '') {
      if (searchTimer !== null) {
        clearTimeout(searchTimer);
        searchTimer = null;
      }
      selection.value = emptySelection();
      rebuildEmptyList();
      invalid.value = false;
      return;
    }

    if (originalLabelChanged.value) {
      await scheduleSearch(v);
      return;
    }

    rebuildEmptyList();
  };

  const acceptHint = (): void => {
    const first = optionsList.value[0]?.items[0];
    if (inputHint.value !== '' && first) {
      selectDestination(first);
    }
  };

  const clear = (): void => {
    inputString.value = '';
    selection.value = emptySelection();
    rebuildEmptyList();
    errorText.value = '';
    invalid.value = false;
  };

  const clearInput = (): void => {
    inputString.value = '';
    rebuildEmptyList();
    errorText.value = '';
    invalid.value = false;
  };

  const validate = (): boolean => {
    if (isNone(selection.value)) {
      invalid.value = true;
      errorText.value =
        inputString.value === '' ? DESTINATION_TEXTS.errorEmpty : DESTINATION_TEXTS.errorInvalid;
      return false;
    }
    errorText.value = '';
    return true;
  };

  const restore = (sel: Selection): void => {
    selection.value = sel;
    inputString.value = buildLabel(sel);
    controlActivated.value = false;
    const country = getSelectedCountry(sel);
    if (country) {
      void loadCountryResorts(country).then((resorts) => {
        if (
          resorts.length > 0 &&
          isCountry(selection.value) &&
          selection.value.country.id === country.id &&
          selection.value.resorts.length === 0
        ) {
          selection.value = selectCountryTransition(country, resorts);
        }
      });
    }
  };

  const setCountries = (list: Country[]): void => {
    countries.value = list.map(withCountryPrice);
    if (controlActivated.value && inputString.value === '') {
      rebuildEmptyList();
    }
  };
  const repriceResorts = (): void => {
    if (currentCountryResortsSource.value.length === 0) {
      return;
    }
    const repriced = currentCountryResortsSource.value.map(withResortPrice);
    currentCountryResorts.value = repriced;

    const byId = new Map<number, Resort>(repriced.map((r) => [r.id, r]));
    let changed = false;
    const next = optionsList.value.map((group) => {
      if (group.kind !== 'resort') {
        return group;
      }
      const items = group.items.map((item) => {
        if (item.type !== 'resort') {
          return item;
        }
        const fresh = byId.get(item.id);
        if (fresh && fresh.minPrice !== item.minPrice) {
          changed = true;
          return { type: 'resort' as const, ...fresh };
        }
        return item;
      });
      return { ...group, items };
    });

    if (changed) {
      optionsList.value = next;
    }
  };

  return {
    inputString,
    selection,
    optionsList,
    controlActivated,
    loading,
    invalid,
    errorText,
    scrollToTopSignal,
    selectedCountry,
    selectedResorts,
    selectedHotels,
    firstOptionName,
    label,
    inputHint,
    originalLabelChanged,
    selectAllVisible,
    addMulti,
    currentInputSelected,
    clearVisible,
    selectDestination,
    selectCountry,
    toggleResort,
    selectAllResorts,
    selectHotel,
    open,
    close,
    setInput,
    acceptHint,
    clear,
    clearInput,
    validate,
    restore,
    setCountries,
    repriceResorts,
  };
}
